import React, { useMemo, useState, memo, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Play, TrendingUp, TrendingDown, Minus, Target, CheckCircle, BarChart2, Lightbulb, ChevronDown, ChevronRight, Zap, RefreshCw } from 'lucide-react';
import { type ModuleData, getModuleIcon, getModuleDisplayInfo, getLearningGoal, getFamilyBySubSkill, getModuleHexColor, getModuleColors } from '../../content';
import { ModuleProgress } from '../../app/types';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/Progress';
import { useMediaQuery } from 'react-responsive';
import { GroupId } from '../../config/systemConfig';
import { useAppStore } from '../../app/hooks/useAppStore';
import { computeTierProgress, computeSkillMasteries } from '../../mastery';
import { formatSkill } from '../../utils/formatters';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
import { useSwipeToDismiss } from '../../app/hooks/useSwipeToDismiss';
interface ModulePanelProps {
  isOpen: boolean;
  onClose: () => void;
  module: ModuleData | null;
  progress: ModuleProgress | null;
  onStartPractice: (moduleId: string) => void;
  onViewExplanation: (moduleId: string) => void;
  selectedGroup: GroupId;
}
export const ModulePanel = memo(function ModulePanel({
  isOpen,
  onClose,
  module,
  progress,
  onStartPractice,
  onViewExplanation,
  selectedGroup
}: ModulePanelProps) {
  const isMobile = useMediaQuery({
    maxWidth: 768
  });
  const focusTrapRef = useFocusTrap(isOpen);
  useEscapeKey(onClose, isOpen);
  useBodyScrollLock(isOpen);
  const swipe = useSwipeToDismiss({
    onDismiss: onClose,
    direction: isMobile ? 'bottom' : 'right',
    disabled: !isOpen
  });
  const {
    state,
    getStartingLevel,
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  // Get dynamic content
  const displayInfo = module ? getModuleDisplayInfo(module.id, selectedGroup) : null;
  const title = displayInfo?.title || module?.title;
  const description = displayInfo?.description || module?.description;
  // v10.0: Use group-scoped mastery
  const masteryPercentage = module ? getModuleMasteryForGroup(module.id) : 0;
  // Compute skill-level aggregated masteries
  // FIX 1: Pass state.familyMastery to computeSkillMasteries
  const skillMasteries = useMemo(() => {
    if (!module) return [];
    return computeSkillMasteries(module.id, selectedGroup, state.questionStates, state.familyMastery);
  }, [module, selectedGroup, state.questionStates, state.familyMastery]);
  // FIX 2: Fallback for answered count (handles demo accounts with synthetic question IDs)
  const answeredCount = useMemo(() => {
    if (!module) return 0;
    const baseCount = getAnsweredQuestions(module.id).length;
    if (baseCount > 0) return baseCount;
    // Fallback: sum from skillMasteries (which now derives it from familyMastery)
    return skillMasteries.reduce((sum, skill) => sum + skill.questionsAnswered, 0);
  }, [module, getAnsweredQuestions, skillMasteries]);
  if (!module || !progress) return null;
  const IconComponent = getModuleIcon(module.icon);
  const bgColorMap: Record<string, string> = {
    'text-blue-500': 'bg-blue-50 text-blue-600 border-blue-100',
    'text-indigo-500': 'bg-indigo-50 text-indigo-600 border-indigo-100',
    'text-emerald-500': 'bg-emerald-50 text-emerald-600 border-emerald-100',
    'text-rose-500': 'bg-rose-50 text-rose-600 border-rose-100',
    'text-amber-500': 'bg-amber-50 text-amber-600 border-amber-100',
    'text-purple-500': 'bg-purple-50 text-purple-600 border-purple-100',
    'text-teal-500': 'bg-teal-50 text-teal-600 border-teal-100',
    'text-cyan-500': 'bg-cyan-50 text-cyan-600 border-cyan-100',
    'text-orange-500': 'bg-orange-50 text-orange-600 border-orange-100',
    'text-sky-500': 'bg-sky-50 text-sky-600 border-sky-100',
    'text-yellow-500': 'bg-yellow-50 text-yellow-600 border-yellow-100',
    'text-fuchsia-500': 'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-100'
  };
  const themeClasses = bgColorMap[module.color] || 'bg-slate-50 text-slate-600 border-slate-100';
  const moduleHex = getModuleHexColor(module.color);
  const moduleColorSet = getModuleColors(module.color);
  // Compute Tier Progress for "Current Level" display
  const startingTier = getStartingLevel(module.id, selectedGroup) || 1;
  const tierProgressInfo = computeTierProgress(module.id, selectedGroup, state.questionStates, startingTier);
  const currentTier = tierProgressInfo.tierDetails.find(t => t.isCurrent) || tierProgressInfo.tierDetails[0];
  // FIX 3: Use title instead of label for user-friendly display (e.g., "Midden Groep 7" instead of "M7")
  const tierDisplay = currentTier?.title || currentTier?.label || '-';
  // Sort skills: Struggling first, then by lowest mastery
  const sortedSkills = [...skillMasteries].sort((a, b) => {
    if (a.trend === 'struggling' && b.trend !== 'struggling') return -1;
    if (b.trend === 'struggling' && a.trend !== 'struggling') return 1;
    return a.masteryScore - b.masteryScore;
  });
  // Find struggling skill for Smart Tip
  const strugglingSkill = sortedSkills.find(s => s.trend === 'struggling' || s.masteryScore < 35);
  // Find decaying skills for Review Tip
  const decayingSkills = sortedSkills.filter(s => s.hasDecay);
  return <AnimatePresence data-id="element-1614">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" data-id="element-1615" />

          {/* Panel */}
          <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="module-panel-title" initial={isMobile ? {
        y: '100%'
      } : {
        x: '100%'
      }} animate={isMobile ? {
        y: 0
      } : {
        x: 0
      }} exit={isMobile ? {
        y: '100%'
      } : {
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} {...swipe.dragProps} className={`
              fixed z-50 bg-[#FAFAF8] shadow-2xl flex flex-col overflow-hidden
              ${isMobile ? 'inset-x-0 bottom-0 h-[92vh] rounded-t-[2rem]' : 'inset-y-0 right-0 w-full max-w-md border-l border-white/50'}
            `} data-id="element-1616">
            {/* Colored Accent Line */}
            <div className="h-1.5 w-full shrink-0" style={{
          backgroundColor: moduleHex
        }} data-id="element-1617" />

            {/* Mobile Drag Handle — only this region initiates swipe-to-dismiss
                so the inner scrollable content keeps scrolling on touch. */}
            {isMobile && <div {...swipe.handleProps} onClick={onClose} className="w-full flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing" role="button" aria-label="Sleep omlaag of tik om te sluiten" data-id="element-1618">
                <div className="w-12 h-1.5 bg-slate-300/50 rounded-full" data-id="element-1619" />
              </div>}

            {/* Compact Header */}
            <div className="px-6 py-4 flex items-start justify-between bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-10" data-id="element-1620">
              <div className="flex items-start gap-4 pr-4 w-full" data-id="element-1621">
                <div className={`p-3 rounded-2xl ${themeClasses} shadow-sm border shrink-0`} data-id="element-1622">
                  <IconComponent className="w-7 h-7" strokeWidth={2.5} data-id="element-1623" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1624">
                  <h2 id="module-panel-title" className="text-2xl font-black text-slate-900 tracking-tight leading-tight" data-id="element-1625">
                    {title}
                  </h2>
                  <p className="text-slate-500 font-medium text-sm leading-snug mt-0.5 line-clamp-2 mb-2.5" data-id="element-1626">
                    {description}
                  </p>

                  {/* Inline Stats Badges */}
                  <div className="flex flex-wrap items-center gap-2" data-id="element-1627">
                    {/* Mastery Badge */}
                    <div className="flex items-center gap-1.5 bg-slate-100 px-2 py-1 rounded-md border border-slate-200" data-id="element-1628">
                      <div className="w-2.5 h-2.5 rounded-full" style={{
                    backgroundColor: masteryPercentage > 0 ? moduleHex : '#e2e8f0'
                  }} data-id="element-1629" />
                      <span className="text-xs font-bold text-slate-700" data-id="element-1630">
                        {masteryPercentage > 0 ? `${Math.round(masteryPercentage)}%` : '0%'}
                      </span>
                    </div>

                    {/* Niveau Badge */}
                    <div className="flex items-center gap-1.5 bg-slate-100 px-2 py-1 rounded-md border border-slate-200" data-id="element-1631">
                      <Target className="w-3 h-3 text-slate-500" data-id="element-1632" />
                      <span className="text-xs font-bold text-slate-700" data-id="element-1633">
                        {tierDisplay}
                      </span>
                    </div>

                    {/* Vragen Badge */}
                    <div className="flex items-center gap-1.5 bg-slate-100 px-2 py-1 rounded-md border border-slate-200" data-id="element-1634">
                      <CheckCircle className="w-3 h-3 text-slate-500" data-id="element-1635" />
                      <span className="text-xs font-bold text-slate-700" data-id="element-1636">
                        {answeredCount}{' '}
                        <span className="font-medium text-slate-500" data-id="element-1637">
                          vragen
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Small inline progress bar */}
                  <div className="mt-3 w-full bg-slate-100 rounded-full h-1.5 overflow-hidden" data-id="element-1638">
                    <div className="h-full rounded-full transition-all duration-500" style={{
                  width: `${masteryPercentage}%`,
                  backgroundColor: masteryPercentage > 0 ? moduleHex : '#e2e8f0'
                }} data-id="element-1639" />
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors shrink-0" aria-label="Paneel sluiten" data-id="element-1640">
                <X className="w-5 h-5" data-id="element-1641" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 flex flex-col scroll-smooth" data-id="element-1642">
              <div className="flex flex-col h-full space-y-6" data-id="element-1643">
                {/* 1. Smart Tip */}
                {strugglingSkill && <div className="bg-amber-50 border border-amber-200/60 rounded-2xl p-4 flex gap-3 shadow-sm" data-id="element-1644">
                    <div className="shrink-0 mt-0.5" data-id="element-1645">
                      <div className="bg-white p-2 rounded-xl shadow-sm text-amber-500 border border-amber-100" data-id="element-1646">
                        <Lightbulb className="w-5 h-5" data-id="element-1647" />
                      </div>
                    </div>
                    <div data-id="element-1648">
                      <h4 className="font-bold text-amber-900 text-sm mb-0.5" data-id="element-1649">
                        Focus Tip
                      </h4>
                      <p className="text-sm text-amber-800/80 font-medium leading-snug" data-id="element-1650">
                        <span className="font-bold capitalize text-amber-900" data-id="element-1651">
                          {formatSkill(strugglingSkill.skill)}
                        </span>{' '}
                        kan nog wat extra aandacht gebruiken.
                      </p>
                    </div>
                  </div>}

                {/* 2b. Review Tip (Decay) */}
                {decayingSkills.length > 0 && <div className="bg-teal-50 border border-teal-200/60 rounded-2xl p-4 flex gap-3 shadow-sm" data-id="element-1652">
                    <div className="shrink-0 mt-0.5" data-id="element-1653">
                      <div className="bg-white p-2 rounded-xl shadow-sm text-teal-500 border border-teal-100" data-id="element-1654">
                        <RefreshCw className="w-5 h-5" data-id="element-1655" />
                      </div>
                    </div>
                    <div data-id="element-1656">
                      <h4 className="font-bold text-teal-900 text-sm mb-0.5" data-id="element-1657">
                        Herhaling Nodig
                      </h4>
                      <p className="text-sm text-teal-800/80 font-medium leading-snug" data-id="element-1658">
                        Je hebt{' '}
                        <strong className="text-teal-900" data-id="element-1659">
                          {decayingSkills.length}{' '}
                          {decayingSkills.length === 1 ? 'vaardigheid' : 'vaardigheden'}
                        </strong>{' '}
                        al een tijdje niet geoefend.
                      </p>
                    </div>
                  </div>}

                {/* 3. Skill Mastery Section */}
                <div data-id="element-1660">
                  <div className="flex items-center gap-2 mb-4" data-id="element-1661">
                    <BarChart2 className="w-5 h-5 text-slate-400" data-id="element-1662" />
                    <h3 className="text-lg font-black text-slate-800" data-id="element-1663">
                      Jouw Vaardigheden
                    </h3>
                  </div>

                  {sortedSkills.length > 0 ? <div className="space-y-3" data-id="element-1664">
                      {sortedSkills.map(skill => {
                  const isMastered = skill.masteryScore >= 85;
                  const isGood = skill.masteryScore >= 50;
                  const isExpanded = expandedSkill === skill.skill;
                  const hasMultipleSubSkills = skill.subSkills.length > 1;
                  let colorClass = 'bg-amber-500';
                  let bgClass = 'bg-white border-slate-200/60';
                  let textClass = 'text-slate-700';
                  let scoreClass = 'text-amber-600 bg-amber-50';
                  if (isMastered) {
                    colorClass = 'bg-emerald-500';
                    textClass = 'text-slate-800';
                    scoreClass = 'text-emerald-700 bg-emerald-50';
                  } else if (isGood) {
                    colorClass = 'bg-orange-500';
                    textClass = 'text-slate-800';
                    scoreClass = 'text-orange-700 bg-orange-50';
                  }
                  return <div key={skill.skill} className={`rounded-xl border ${bgClass} shadow-sm overflow-hidden transition-all`} data-id="element-1665">
                            {/* Skill Row */}
                            <div className={`p-3.5 flex items-center justify-between gap-3 ${hasMultipleSubSkills ? 'cursor-pointer hover:bg-slate-50/50' : ''}`} onClick={() => {
                      if (hasMultipleSubSkills) {
                        setExpandedSkill(isExpanded ? null : skill.skill);
                      }
                    }} data-id="element-1666">
                              <div className="flex-1" data-id="element-1667">
                                <div className="flex items-center justify-between mb-2" data-id="element-1668">
                                  <div className="flex items-center gap-2" data-id="element-1669">
                                    {hasMultipleSubSkills && <div className="text-slate-400" data-id="element-1670">
                                        {isExpanded ? <ChevronDown className="w-4 h-4" data-id="element-1671" /> : <ChevronRight className="w-4 h-4" data-id="element-1672" />}
                                      </div>}
                                    <span className={`font-bold text-sm capitalize ${textClass}`} data-id="element-1673">
                                      {formatSkill(skill.skill)}
                                    </span>
                                    {skill.hasDecay && <div className="bg-teal-100 text-teal-700 p-1 rounded-full" title="Herhaling nodig" data-id="element-1674">
                                        <RefreshCw className="w-3 h-3" data-id="element-1675" />
                                      </div>}
                                  </div>

                                  <div className="flex items-center gap-2" data-id="element-1676">
                                    {skill.trend === 'improving' && <TrendingUp className="w-3.5 h-3.5 text-emerald-500" data-id="element-1677" />}
                                    {skill.trend === 'struggling' && <TrendingDown className="w-3.5 h-3.5 text-amber-500" data-id="element-1678" />}
                                    {skill.trend === 'stable' && <Minus className="w-3.5 h-3.5 text-slate-300" data-id="element-1679" />}
                                    <span className={`text-xs font-black px-1.5 py-0.5 rounded-md ${scoreClass}`} data-id="element-1680">
                                      {skill.masteryScore}%
                                    </span>
                                  </div>
                                </div>
                                <ProgressBar value={skill.masteryScore} height={4} color={colorClass} className="bg-slate-100" data-id="element-1681" />
                              </div>
                            </div>

                            {/* SubSkill Drill-Down */}
                            <AnimatePresence data-id="element-1682">
                              {isExpanded && hasMultipleSubSkills && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-hidden bg-slate-50/80 border-t border-slate-100" data-id="element-1683">
                                  <div className="p-2 space-y-1.5" data-id="element-1684">
                                    {skill.subSkills.sort((a, b) => a.masteryScore - b.masteryScore).map(sub => {
                            const family = getFamilyBySubSkill(sub.subSkill);
                            const learningGoal = family ? getLearningGoal(family.familyId) : '';
                            return <div key={sub.subSkill} className="flex flex-col bg-white rounded-lg px-3 py-2 border border-slate-100 shadow-sm" data-id="element-1685">
                                            <div className="flex items-center justify-between" data-id="element-1686">
                                              <span className="text-xs font-medium text-slate-600 capitalize" data-id="element-1687">
                                                {formatSkill(sub.subSkill)}
                                              </span>
                                              <div className="flex items-center gap-2" data-id="element-1688">
                                                {sub.hasDecay && <RefreshCw className="w-3 h-3 text-teal-500" title="Herhaling nodig" data-id="element-1689" />}
                                                <span className="text-xs font-bold text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded" data-id="element-1690">
                                                  {sub.masteryScore}%
                                                </span>
                                              </div>
                                            </div>
                                            {learningGoal && <div className="text-[11px] text-slate-400 font-normal mt-0.5 flex items-start gap-1" data-id="element-1691">
                                                <Target className="w-3 h-3 shrink-0 mt-[1px] text-slate-300" data-id="element-1692" />
                                                <span data-id="element-1693">{learningGoal}</span>
                                              </div>}
                                          </div>;
                          })}
                                  </div>
                                </motion.div>}
                            </AnimatePresence>
                          </div>;
                })}
                    </div> : <div className="text-center py-8 bg-white rounded-2xl border border-dashed border-slate-300" data-id="element-1694">
                      <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-1695">
                        <Zap className="w-5 h-5 text-slate-400" data-id="element-1696" />
                      </div>
                      <p className="text-slate-500 font-medium text-sm" data-id="element-1697">
                        Start met oefenen om je vaardigheden te zien!
                      </p>
                    </div>}
                </div>

                {/* Bottom padding for sticky actions */}
                <div className="h-24 shrink-0" data-id="element-1698" />
              </div>
            </div>

            {/* Sticky Bottom Actions */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#FAFAF8] via-[#FAFAF8] to-transparent pt-16 pointer-events-none" data-id="element-1699">
              <div className="flex flex-col gap-3 pointer-events-auto" data-id="element-1700">
                <motion.button whileTap={{
              scale: 0.97
            }} onClick={() => onStartPractice(module.id)} className="w-full flex items-center justify-center font-bold rounded-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 hover:-translate-y-0.5 active:translate-y-[2px] active:shadow-none shadow-lg h-14 text-base group text-white" style={{
              backgroundColor: moduleHex,
              boxShadow: `0 4px 0 0 ${moduleHex}80, 0 10px 15px -3px ${moduleHex}40`
            }} data-id="element-1701">
                  <span className="font-black mr-2" data-id="element-1702">Start Oefensessie</span>
                  <div className="bg-white/20 rounded-md p-1 group-hover:bg-white/30 transition-colors" data-id="element-1703">
                    <Play className="w-4 h-4 fill-current" data-id="element-1704" />
                  </div>
                </motion.button>

                <Button onClick={() => onViewExplanation(module.id)} variant="ghost" size="lg" fullWidth className="text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 h-12" data-id="element-1705">
                  <Lightbulb className="w-4 h-4 mr-2" data-id="element-1706" />
                  <span className="font-bold" data-id="element-1707">Wat leer je hier?</span>
                </Button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
});